// src/services/teamService.js
// Perfil / Team helpers (Supabase).
// Basado en tu HTML legacy: lee de la tabla `profiles` los campos `role, team_id, display_name`
// filtrando por `user_id = session.user.id`.
//
// Objetivo:
// - Centralizar obtención de profile y team_id
// - Cachear en memoria para no spamear Supabase
// - No acoplarse a UI ni al store (eso vendrá después)

import { getSupabaseClient, getUser as getSupabaseUser, getSession as getSupabaseSession } from "./supabaseClient.js";
import { ROLE_PERMISSIONS } from "../domain/constants.js";


const DEFAULT_PROFILE_SELECT = "role, team_id, display_name, dni, active";
const DEFAULT_ROLE = "viewer";

// Cache simple en memoria (por usuario)
const _cache = {
  byUserId: new Map(), // userId -> { profile, fetchedAt }
};

function nowMs() {
  return Date.now();
}

/**
 * Limpia la cache (útil en signOut o cuando sospeches cambios).
 */
export function clearTeamCache() {
  _cache.byUserId.clear();
}

/**
 * Obtiene el profile (role/team_id/display_name) para un user_id.
 *
 * options:
 * - select: string (campos a seleccionar)
 * - force: boolean (ignora cache)
 * - ttlMs: number (tiempo de vida del cache)
 * - allowNull: boolean (si no existe profile, devuelve null en vez de lanzar)
 */
export async function getProfileByUserId(userId, options = {}) {
  const {
    select = DEFAULT_PROFILE_SELECT,
    force = false,
    ttlMs = 60_000, // 1 min cache por defecto
    allowNull = true,
  } = options;

  if (!userId) throw new Error("getProfileByUserId: userId es obligatorio.");

  // Cache check
  if (!force) {
    const cached = _cache.byUserId.get(userId);
    if (cached && nowMs() - cached.fetchedAt <= ttlMs) {
      return cached.profile;
    }
  }

  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from("profiles")
    .select(select)
    .eq("user_id", userId)
    .single();

  if (error) {
    // Si no existe fila, Supabase devuelve "PGRST116".
    // Si es otro error (ej. RLS), no queremos cachear un null permanente.
    if (error.code === "PGRST116" && allowNull) {
      _cache.byUserId.set(userId, { profile: null, fetchedAt: nowMs() });
      return null;
    }
    // Si allowNull es true pero es un error de permisos u otro, no cacheamos null
    if (allowNull) return null;
    throw error;
  }

  const profile = data || null;

  _cache.byUserId.set(userId, { profile, fetchedAt: nowMs() });
  return profile;
}

/**
 * Devuelve team_id para un user_id.
 *
 * options:
 * - force/ttlMs/allowNull igual que getProfileByUserId
 */
export async function getTeamId(userId, options = {}) {
  const profile = await getProfileByUserId(userId, options);

  const teamId = profile?.team_id ?? null;
  if (!teamId && options?.allowNull === false) {
    throw new Error("No hay team_id asociado al usuario en profiles.");
  }
  return teamId;
}

/**
 * Devuelve role para un user_id (default: 'viewer' si no existe).
 */
export async function getRole(userId, options = {}) {
  const profile = await getProfileByUserId(userId, options);
  return profile?.role || DEFAULT_ROLE;
}

/**
 * Devuelve display_name para un user_id (o null).
 */
export async function getDisplayName(userId, options = {}) {
  const profile = await getProfileByUserId(userId, options);
  return profile?.display_name ?? null;
}

/**
 * Helper: obtiene directamente el usuario logueado de Supabase.
 * Para el router y UI, preferimos la sesión local (rápida y silenciosa).
 */
export async function getUser() {
  try {
    const session = await getSupabaseSession();
    return session?.user || null;
  } catch (e) {
    // Silencioso para evitar spam en consola si no hay sesión
    return null;
  }
}


/**
 * Helper: obtiene el user actual desde Supabase auth y devuelve { user, profile }.
 * options:
 * - profileOptions: opciones pasadas a getProfileByUserId
 */
export async function getCurrentUserAndProfile(options = {}) {
  const supabase = getSupabaseClient();

  // getSession() lee la sesión local (rápido y sin "Auth session missing" por arranque)
  const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();

  if (sessionErr) {
    console.warn("[teamService] getSession error:", sessionErr.message);
    return { user: null, profile: null };
  }

  const session = sessionData?.session ?? null;
  const user = session?.user ?? null;

  // Esto NO es error: usuario no logueado o sesión aún no restaurada
  if (!user) return { user: null, profile: null };

  const profile = await getProfileByUserId(user.id, options.profileOptions || {});
  return { user, profile };
}


export async function getCurrentTeamId(options = {}) {
  const { user, profile } = await getCurrentUserAndProfile(options);
  return profile?.team_id ?? null;
}

/**
 * Obtiene el ID de miembro (team_members.id) del usuario actual para un team dado.
 * Necesario porque events.owner_id apunta a team_members.id, no a auth.users.id.
 */
export async function getCurrentTeamMemberId(teamId) {
  if (!teamId) return null;
  const { user } = await getCurrentUserAndProfile();
  if (!user) return null;

  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from("team_members")
    .select("id")
    .eq("team_id", teamId)
    .eq("auth_user_id", user.id) // Corrected from member_id to auth_user_id
    .maybeSingle();

  if (error) {
    console.warn("Error fetching team_member id:", error);
    return null;
  }
  return data?.id || null;
}

/**
 * Lista todos los miembros (perfiles) de un equipo.
 * Retorna array de { user_id, display_name, role, ... }
 */
export async function listTeamMembers(teamId) {
  if (!teamId) {
    console.warn('[teamService] listTeamMembers called with no teamId');
    return [];
  }

  const supabase = getSupabaseClient();

  // [DEBUG] Log the query being executed
  console.log('[teamService] Querying profiles table for team_id:', teamId);

  const { data, error } = await supabase
    .from("profiles")
    .select("user_id, display_name, role")
    .eq("team_id", teamId)
    .order("display_name", { ascending: true });

  if (error) {
    console.error('[teamService] Error fetching team members:', error);
    throw error;
  }

  // [DEBUG] Log the results
  console.log('[teamService] Team members found:', {
    count: data?.length || 0,
    members: data || []
  });

  return data || [];
}

/**
 * Lista todos los equipos a los que el usuario tiene acceso (basado en la tabla profiles).
 */
export async function listUserTeams() {
  const supabase = getSupabaseClient();

  const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
  if (sessionErr) return [];

  const user = sessionData?.session?.user;
  if (!user) return [];

  const { data, error } = await supabase
    .from("teams")
    .select("id, name")
    .order("name", { ascending: true });

  if (error) {
    console.error("[teamService] Error fetching teams:", error);
    throw error;
  }

  return data || [];
}

// =========================
// Permission helpers
// =========================

/**
 * Devuelve el objeto de permisos para un rol dado.
 */
export function getPermissions(role) {
  return ROLE_PERMISSIONS[role] || ROLE_PERMISSIONS.viewer;
}

/**
 * Devuelve los permisos del usuario actual.
 */
export async function getCurrentPermissions() {
  const { user, profile } = await getCurrentUserAndProfile();
  if (!user) return ROLE_PERMISSIONS.viewer;
  return getPermissions(profile?.role || DEFAULT_ROLE);
}

// =========================
// Profile update helpers
// =========================

/**
 * Actualiza el display_name del perfil del usuario.
 */
export async function updateProfile(userId, updates = {}) {
  if (!userId) throw new Error("updateProfile: userId es obligatorio.");

  const supabase = getSupabaseClient();
  const allowedFields = ['display_name', 'dni'];
  const patch = {};
  for (const key of allowedFields) {
    if (updates[key] !== undefined) patch[key] = updates[key];
  }

  if (Object.keys(patch).length === 0) throw new Error("No hay campos válidos para actualizar.");

  const { data, error } = await supabase
    .from("profiles")
    .update(patch)
    .eq("user_id", userId)
    .select(DEFAULT_PROFILE_SELECT)
    .single();

  if (error) throw error;

  // Invalidar cache
  _cache.byUserId.delete(userId);

  return data;
}

/**
 * Cambia la contraseña del usuario autenticado.
 */
export async function changePassword(newPassword) {
  if (!newPassword || newPassword.length < 6) {
    throw new Error("La contraseña debe tener al menos 6 caracteres.");
  }

  const supabase = getSupabaseClient();
  const { data, error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) throw error;
  return data;
}

// =========================
// Admin / Team Management
// =========================

/**
 * Updates the role of a specific team member.
 * Only callable by admins (UI should enforce this, RLS should enforce this).
 */
export async function updateMemberRole(targetUserId, newRole) {
  if (!targetUserId) throw new Error("Target User ID is required");
  if (!["admin", "manager", "viewer"].includes(newRole)) {
    throw new Error("Rol no permitido por la base de datos actual.");
  }

  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from("profiles")
    .update({ role: newRole })
    .eq("user_id", targetUserId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Removes a member from the team (sets team_id to null and role to viewer).
 * Effectively "kicking" them from the organization view.
 */
export async function removeMemberFromTeam(targetUserId) {
  if (!targetUserId) throw new Error("Target User ID is required");
  throw new Error("La base de datos actual no permite quitar miembros: profiles.team_id es obligatorio.");
}
